import { useEffect, useId, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { IconCheck, IconPlus, IconSwitch } from "@/components/Icons";
import { SportGlyph } from "@/components/sidebar/SportGlyph";
import { displayName } from "@/lib/format";

import styles from "./SportSwitcher.module.css";

export type SwitcherSport = {
  code: string;
  published: boolean;
  /** The sport this one sits inside — Aquatics holds swimming. */
  partOf?: string | null;
  /** Whether it holds disciplines rather than fixtures. */
  groups?: boolean;
};

/**
 * Which sport you are working in, and how you change it.
 *
 * A `<select>` was doing this job. It reads as a form field in the middle of a
 * menu, it cannot show what a sport *is* — published or draft, a discipline or a
 * grouping — and it cannot be reached without the mouse leaving the keyboard.
 *
 * So: the header itself is the trigger, and the panel is a proper list. Each row
 * carries the sport's own mark, its name, and one line saying where it sits.
 * ⌘1…⌘9 switch straight to a sport from anywhere, which is the whole reason the
 * shortcuts are printed on the rows rather than hidden in a help page.
 *
 * The panel is dark on purpose. It is the one control that changes what every
 * other row in the sidebar means, and inverting it says "this is a different
 * kind of thing" far more quickly than a heading would.
 */
export function SportSwitcher({
  sports,
  current,
  onPick,
}: {
  sports: SwitcherSport[];
  current: string | undefined;
  /** Called with `undefined` for "All sports". */
  onPick: (code: string | undefined) => void;
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const wrap = useRef<HTMLDivElement>(null);

  const chosen = sports.find((s) => s.code === current);

  // Close on outside click and on Escape — a panel that traps you is worse than
  // no panel.
  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (!wrap.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // ⌘1…⌘9 anywhere. Bound at the document because the point is to switch sport
  // without first going to find the switcher.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey) return;
      const index = Number(event.key) - 1;
      if (!Number.isInteger(index) || index < 0 || index >= Math.min(sports.length, 9)) return;
      event.preventDefault();
      setOpen(false);
      onPick(sports[index].code);
      navigate(`/${sports[index].code}`);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [sports, onPick, navigate]);

  const go = (code: string | undefined) => {
    setOpen(false);
    onPick(code);
    navigate(code ? `/${code}` : "/sports");
  };

  /** What a sport *is*, in one line. The reference design puts a member count
   *  here; a sport has no members, and its place in the hierarchy is the fact
   *  that actually changes how the rest of the menu behaves. */
  const subtitle = (sport: SwitcherSport): string => {
    const parts: string[] = [];
    if (sport.groups) parts.push("Holds disciplines");
    else if (sport.partOf) parts.push(`In ${displayName(sport.partOf)}`);
    else parts.push("Sport");
    if (!sport.published) parts.push("draft");
    return parts.join(" · ");
  };

  return (
    <div className={styles.wrap} ref={wrap}>
      <button
        type="button"
        className={open ? `${styles.trigger} ${styles.triggerOpen}` : styles.trigger}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => setOpen((was) => !was)}
      >
        <SportGlyph code={chosen?.code} size={30} />
        <span className={styles.triggerText}>
          <span className={styles.triggerName}>
            {chosen ? displayName(chosen.code) : "All sports"}
          </span>
          <span className={styles.triggerNote}>
            {chosen ? subtitle(chosen) : `${sports.length} in the taxonomy`}
          </span>
        </span>
        <span className={styles.triggerChevron} aria-hidden>
          <IconSwitch size={15} />
        </span>
      </button>

      {open && (
        <div className={styles.panel} id={panelId} role="listbox" aria-label="Switch sport">
          <p className={styles.panelHead}>Switch sport</p>

          <div className={styles.rows}>
            {sports.map((sport, index) => {
              const isCurrent = sport.code === current;
              return (
                <button
                  key={sport.code}
                  type="button"
                  role="option"
                  aria-selected={isCurrent}
                  className={styles.row}
                  onClick={() => go(sport.code)}
                >
                  <SportGlyph code={sport.code} size={34} />
                  <span className={styles.rowText}>
                    <span className={styles.rowName}>{displayName(sport.code)}</span>
                    <span className={styles.rowNote}>{subtitle(sport)}</span>
                  </span>
                  {isCurrent ? (
                    <span className={styles.tick} aria-hidden>
                      <IconCheck size={13} />
                    </span>
                  ) : (
                    index < 9 && <kbd className={styles.shortcut}>⌘{index + 1}</kbd>
                  )}
                </button>
              );
            })}
          </div>

          <div className={styles.rule} />

          <button
            type="button"
            className={`${styles.row} ${styles.rowQuiet}`}
            onClick={() => go(undefined)}
          >
            <span className={styles.rowIcon} aria-hidden>
              <IconSwitch size={15} />
            </span>
            <span className={styles.rowText}>
              <span className={styles.rowName}>All sports</span>
            </span>
          </button>

          <button
            type="button"
            className={`${styles.row} ${styles.rowQuiet}`}
            onClick={() => {
              setOpen(false);
              navigate("/sports/new");
            }}
          >
            <span className={styles.rowIcon} aria-hidden>
              <IconPlus size={15} />
            </span>
            <span className={styles.rowText}>
              <span className={styles.rowName}>Onboard a sport</span>
            </span>
          </button>
        </div>
      )}
    </div>
  );
}
